/**
 * Résolution d'un `SERVER_UPDATED` — le serveur signale que le client porte un
 * `serverBootId` (= GIT_SHA) différent du sien.
 *
 * Contexte : pendant un déploiement, les workers sont roulés un par un et
 * l'upstream API n'a aucune affinité de session (`least_conn`). Un même joueur
 * voit donc alterner ancienne et nouvelle version pendant toute la fenêtre de
 * roll. Or le serveur **refuse l'écriture avant toute persistance** sur mismatch
 * (`checkServerBootMismatch` en tête de handler) : recharger la page à cet
 * instant détruit la progression que la requête refusée transportait.
 *
 * D'où la règle appliquée ici : on adopte le boot id renvoyé par le serveur et
 * on rejoue l'écriture **une fois**. Si le rejeu retombe sur un worker de
 * l'autre version, on ne recharge pas — l'état reste en mémoire et le cycle de
 * sauvegarde suivant le persistera une fois la flotte convergée.
 *
 * Le rechargement conserve sa raison d'être (récupérer le nouveau bundle JS),
 * mais il est désormais subordonné à une sauvegarde réellement passée, et plafonné
 * à une occurrence par fenêtre pour éviter la cascade de reloads d'un déploiement.
 */

/** Réponse serveur minimale exploitée ici. */
export interface ServerUpdateResponse {
  reload?: boolean
  code?: string
  serverBootId?: string
}

export type ServerUpdateAction =
  /** Adopter `bootId` puis rejouer l'écriture refusée. */
  | { action: 'retry'; bootId: string }
  /** Renoncer sans recharger : rien n'a été persisté, rien n'est perdu. */
  | { action: 'continue' }

/** Fenêtre pendant laquelle un second reload « serveur mis à jour » est supprimé. */
export const SERVER_UPDATE_RELOAD_COOLDOWN_MS = 10 * 60 * 1000

/** Vrai si la réponse est bien un refus pour cause de version serveur différente. */
export function isServerUpdatedResponse(res: ServerUpdateResponse | null | undefined): boolean {
  return !!res?.reload && res.code === 'SERVER_UPDATED'
}

/**
 * Que faire d'un `SERVER_UPDATED`.
 *
 * @param res      réponse du serveur (porte le boot id à adopter)
 * @param retried  une tentative de rejeu a-t-elle déjà eu lieu pour cette écriture
 */
export function decideServerUpdateResync(
  res: ServerUpdateResponse | null | undefined,
  retried: boolean,
): ServerUpdateAction {
  if (!isServerUpdatedResponse(res)) return { action: 'continue' }

  // Sans boot id exploitable, rejouer produirait le même refus : on renonce.
  const bootId = String(res?.serverBootId ?? '').trim()
  if (!bootId) return { action: 'continue' }

  // Un seul rejeu : au-delà, la flotte est mixte et l'aller-retour serait sans fin.
  if (retried) return { action: 'continue' }

  return { action: 'retry', bootId }
}

/**
 * Le rechargement de page est-il autorisé maintenant ?
 *
 * Deux conditions cumulatives : l'écriture a fini par passer (sinon recharger
 * perdrait ce qu'elle transportait), et aucun reload de ce type n'a eu lieu dans
 * la fenêtre de refroidissement (sinon un déploiement en recharge le joueur à
 * chaque worker roulé).
 */
export function shouldReloadAfterServerUpdate(params: {
  writePersisted: boolean
  lastReloadAtMs: number | null
  nowMs: number
}): boolean {
  if (!params.writePersisted) return false
  const last = params.lastReloadAtMs
  if (last != null && Number.isFinite(last) && params.nowMs - last < SERVER_UPDATE_RELOAD_COOLDOWN_MS) {
    return false
  }
  return true
}
